import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "../../staff/Button";
import ChevronDown from "../../Atomos/Icons/ChevronDown";
import { IHeader } from "./FilteredTable.types";
import {
  COLUMN_VISIBILITY_PANEL_IDS,
  getHeaderTitle,
} from "./FilteredTable.utils";
import { useStaffMode } from "../../../hooks/StaffModeProvider";

interface ColumnVisibilityMenuProps {
  headers: IHeader[];
  columnVisibility: Record<string, boolean>;
  onToggle: (columnId: string) => void;
  onClearAll: () => void;
  onSelectAll: () => void;
}

export default function ColumnVisibilityMenu({
  headers,
  columnVisibility,
  onToggle,
  onClearAll,
  onSelectAll,
}: ColumnVisibilityMenuProps) {
  const [dropdown, setDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isStaffMode } = useStaffMode();

  useEffect(() => {
    if (!dropdown) {
      return;
    }
    const onDocMouseDown = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdown(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [dropdown]);

  const toggleableHeaders = useMemo(
    () => headers.filter((h) => !COLUMN_VISIBILITY_PANEL_IDS.has(h.id)),
    [headers],
  );

  const selectedColumns = useMemo(
    () => toggleableHeaders.filter((h) => columnVisibility[h.id] !== false),
    [toggleableHeaders, columnVisibility],
  );

  const availableColumns = useMemo(
    () => toggleableHeaders.filter((h) => columnVisibility[h.id] === false),
    [toggleableHeaders, columnVisibility],
  );

  const selectedCount = selectedColumns.length;
  const totalToggleable = toggleableHeaders.length;

  const renderColumnRow = (h: IHeader) => {
    const checked = columnVisibility[h.id] !== false;
    const headerTitle = getHeaderTitle(h.header, h.id);
    return (
      <button
        key={h.id}
        type="button"
        role="menuitemcheckbox"
        aria-checked={checked}
        onClick={() => onToggle(h.id)}
        className="flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm text-gray-font transition-colors hover:bg-gray-50"
      >
        <input
          type="checkbox"
          checked={checked}
          readOnly
          tabIndex={-1}
          aria-hidden
          className="pointer-events-none relative h-4 w-4 shrink-0 appearance-none border border-neutral-dark bg-white checked:border-Default checked:bg-accent-Default-light checked:after:border-Default after:absolute after:left-1/2 after:top-1/2 after:h-[7px] after:w-[3px] after:-translate-x-1/2 after:-translate-y-[60%] after:rotate-45 after:border-b after:border-r after:border-transparent after:content-['']"
        />
        <span className="flex-1 truncate">{headerTitle}</span>
      </button>
    );
  };

  return (
    <div
      className="relative flex min-w-0 flex-1 flex-col text-left lg:w-auto lg:flex-none"
      ref={dropdownRef}
    >
      <div className="flex w-full justify-stretch">
        <Button
          text="Table"
          color={isStaffMode ? "secondary" : "primary"}
          variant="outlined"
          size="fixed"
          className="w-full lg:w-auto"
          onClick={() => setDropdown((v) => !v)}
          icon={
            <ChevronDown
              rotation={dropdown ? "rotate(180deg)" : "rotate(0deg)"}
            />
          }
        />
      </div>

      <div
        className={`absolute right-0 top-full z-50 mt-2 w-[min(80vw,2rem)] min-w-[18rem] max-w-[26rem] overflow-hidden rounded-md border border-gray-100 bg-white shadow-xl transition-all duration-100 ease-out ${
          dropdown
            ? "visible translate-y-0 opacity-100"
            : "pointer-events-none invisible -translate-y-1 opacity-0"
        }`}
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="menu-button"
        tabIndex={-1}
      >
        <div className="border-b border-gray-100 px-3 py-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-gray-500 py-2">
              {selectedCount}/{totalToggleable} selected
            </span>
            <div className="flex shrink-0 items-center gap-4">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectAll();
                }}
                disabled={availableColumns.length === 0}
                className="font-medium text-secondary underline transition-colors hover:enabled:text-secondary-dark disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClearAll();
                }}
                disabled={selectedCount === 0}
                className="font-medium text-secondary underline transition-colors hover:enabled:text-secondary-dark disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
              >
                Clear all
              </button>
            </div>
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto py-1">
          {selectedColumns.length > 0 && (
            <>
              <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500">
                <span>Selected columns</span>
                <span className="h-px flex-1 bg-gray-100" />
              </div>
              {selectedColumns.map(renderColumnRow)}
            </>
          )}
          {availableColumns.length > 0 && (
            <>
              <div
                className={`flex items-center gap-2 px-3 py-2 text-xs text-gray-500 ${
                  selectedColumns.length > 0 ? "border-t border-gray-100" : ""
                }`}
              >
                <span>Available columns</span>
                <span className="h-px flex-1 bg-gray-100" />
              </div>
              {availableColumns.map(renderColumnRow)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
